import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import api from '../utils/api';
import { auth } from '../config/firebase';
import { OFFLINE_ENABLED } from '../offline/entities';
import { AuthContext } from '../auth/AuthContext';
import { isSessionExpiredError } from '../auth/sessionPolicy';
import {
  isTransportError,
  isEffectivelyOnline,
  reportFromError,
  subscribeConnectivity,
} from '../offline/connectivity';
const SUBS_ENABLED = process.env.REACT_APP_ENABLE_SUBSCRIPTIONS === 'true';

/**
 * Offline excuse (flag-gated): the client guard is the PRIMARY subscription
 * gate (the backend tier-guards cover only a few controllers), so a
 * connectivity failure may NOT blanket-fail-open — that would let a
 * never-subscribed user in by simply blocking this one endpoint. Instead, the
 * last SERVER verdict is cached per-uid on every successful check, and a
 * transport failure is excused ONLY when this uid was previously verified
 * subscribed on this device. The excuse lasts for the offline window only —
 * the guard re-checks the moment connectivity returns.
 */
const verdictKey = (uid: string) => `st.offline.subOk.${uid}`;

function cacheVerdict(ok: boolean): void {
  if (!OFFLINE_ENABLED) return; // flag-off: write no new prod artifact
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  try {
    localStorage.setItem(verdictKey(uid), ok ? 'true' : 'false');
  } catch {
    /* localStorage unavailable — the excuse just won't be available offline */
  }
}

function hasCachedGoodVerdict(): boolean {
  const uid = auth.currentUser?.uid;
  if (!uid) return false;
  try {
    return localStorage.getItem(verdictKey(uid)) === 'true';
  } catch {
    return false;
  }
}

interface SubscriptionGuardProps {
  children: React.ReactNode;
}

const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);
  // True when the current pass was granted on the offline excuse rather than a
  // live server verdict — drives the reconnect re-check below.
  const [excusedOffline, setExcusedOffline] = useState(false);
  const { reauthCount } = useContext(AuthContext);
  // True while a check is waiting out SESSION_EXPIRED; only such a check re-runs on a re-auth.
  const deferredRef = useRef(false);

  const checkSubscription = useCallback(async () => {
    let deferred = false;
    try {
      const response = await api.get('/subscription/status');
      const { status, hasAccess } = response.data;
      // The server's paid-status verdict (INV-76), which also admits a clinic inside its 7-day
      // card-decline grace; a backend older than that field falls back to ACTIVE/TRIALING.
      const ok = typeof hasAccess === 'boolean' ? hasAccess : status === 'ACTIVE' || status === 'TRIALING';
      setHasSubscription(ok);
      setExcusedOffline(false);
      cacheVerdict(ok); // remember the SERVER verdict for the offline excuse
    } catch (error) {
      if (isSessionExpiredError(error)) {
        // ADR-0017: the 24 hours are up and the password dialog is showing. Not a verdict: keep
        // waiting; the effect below checks again once the password is accepted.
        deferred = true;
        deferredRef.current = true;
        return;
      }
      console.error('Error checking subscription:', error);
      if (OFFLINE_ENABLED) {
        // Prime the observed-connectivity layer: this check often fires before
        // OfflineProvider mounts, so an offline cold-launch flips the app to
        // effective-offline on its very first failed request.
        reportFromError(error);
        if (isTransportError(error) && hasCachedGoodVerdict()) {
          // Offline, and this vet was verified subscribed on this device —
          // let them at their cached charts. Re-validated on reconnect.
          setHasSubscription(true);
          setExcusedOffline(true);
          return;
        }
      }
      // If there's an error, assume no subscription (byte-identical to the
      // pre-offline app when the flag is off).
      setHasSubscription(false);
    } finally {
      if (!deferred) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!SUBS_ENABLED) {
      // Subscriptions disabled: skip API check
      setHasSubscription(true);
      setLoading(false);
      return;
    }
    void checkSubscription();
  }, [checkSubscription]);

  // ADR-0017: the check that met SESSION_EXPIRED runs again once the password is accepted.
  useEffect(() => {
    if (!deferredRef.current) return;
    deferredRef.current = false;
    void checkSubscription();
  }, [reauthCount, checkSubscription]);

  // An excused entry is valid for the offline window ONLY: re-run the real
  // check on the first flip back to effective-online. A genuine server verdict
  // of no-subscription then paywalls as usual.
  useEffect(() => {
    if (!excusedOffline) return;
    return subscribeConnectivity(() => {
      if (isEffectivelyOnline()) void checkSubscription();
    });
  }, [excusedOffline, checkSubscription]);

  // If subscriptions are disabled, bypass paywall entirely
  if (!SUBS_ENABLED) {
    return <>{children}</>;
  }
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // If no subscription, redirect to paywall
  if (!hasSubscription) {
    return <Navigate to="/paywall" replace />;
  }

  // If has subscription, render the protected content
  return <>{children}</>;
};

export default SubscriptionGuard;
